import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { commonToast } from '../../../helper/Toast';
// import { BiImport } from 'react-icons/bi';
import { FaPlus, FaEdit, FaTrash, FaEye } from 'react-icons/fa';
import { IoMdSearch } from 'react-icons/io';
import Select from 'react-select';
import TailwindDataTable from '../../../components/TailwindDataTable';
import PageSizeSelectBox from '../../../components/PageSizeSelectBox';
import ReactPaginate from 'react-paginate';
import AddAdminUserModel from '../../../components/model/AddAdminUserModel';
import EditCreatorModel from '../../../components/model/EditCreatorModel';
import DeleteConfirmationModel from '../../../components/model/DeleteConfirmationModel';
import { deleteContentCreator, fetchAllContentCreators, getApiError, getContentCreatorId } from '../../../helper/adminUsersApi';

export default function CreatorList() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [creators, setCreators] = useState([]);
    const [limit, setLimit] = useState(10);
    const [loading, setLoading] = useState(false);
    const [isAddOpen, setIsAddOpen] = useState(false);
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [editingCreator, setEditingCreator] = useState(null);
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [deletingCreator, setDeletingCreator] = useState(null);

    const columns = [
        {
            key: 'name',
            label: t('creator_management.creator_name'),
            formatter: (_, row) => {
                if (!row) return null;
                const fullName = `${row.firstName || ''} ${row.lastName || ''}`.trim() || row.name || '';
                const initials = fullName ? fullName.split(' ').map(n => n[0]).join('') : '?';
                return (
                    <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                                <span className="text-gray-500 text-sm font-medium">
                                    {initials}
                                </span>
                            </div>
                        </div>
                        <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{fullName}</div>
                            <div className="text-sm text-gray-500">{row.email || ''}</div>
                        </div>
                    </div>
                );
            }
        },
        {
            key: 'totalDecks',
            label: t('creator_management.total_decks'),
            formatter: (cell) => cell || 0
        },
        {
            key: 'totalStudents',
            label: t('creator_management.total_students'),
            formatter: (cell) => cell || 0
        },
        {
            key: 'averageRating',
            label: t('creator_management.average_rating'),
            formatter: (cell) => {
                if (!cell) return 'N/A';
                return `${cell.toFixed(1)}/5.0`;
            }
        },
        {
            key: 'status',
            label: t('creator_management.status'),
            formatter: (cell) => {
                if (!cell) return null;
                return (
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${String(cell).toUpperCase() === 'ACTIVE' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                        {t(`creator_management.${String(cell).toLowerCase()}`, { defaultValue: cell })}
                    </span>
                );
            }
        },
        {
            key: 'lastActivity',
            label: t('creator_management.last_activity'),
            formatter: (cell) => cell || ''
        },
        {
            key: 'actions',
            label: t('creator_management.actions'),
            formatter: (_, row) => {
                return (
                    <div className="flex items-center gap-3">
                        {/* <button
                            onClick={() => handleViewCreator(row)}
                            className="text-green-600 hover:text-green-800 transition-colors duration-200"
                            title={t('creator_management.view')}
                        >
                            <FaEye size={18} />
                        </button> */}
                        <button
                            onClick={() => handleEditCreator(row)}
                            className="text-blue-600 hover:text-blue-800 transition-colors duration-200"
                            title={t('creator_management.edit')}
                        >
                            <FaEdit size={18} />
                        </button>
                        <button
                            onClick={() => handleDeleteCreator(row)}
                            className="text-red-600 hover:text-red-800 transition-colors duration-200"
                            title={t('creator_management.remove')}
                        >
                            <FaTrash size={18} />
                        </button>
                    </div>
                );
            }
        }
    ];

    const fetchCreators = async () => {
        try {
            setLoading(true);
            const list = await fetchAllContentCreators();
            setCreators(list);
            setCurrentPage(0);
        } catch (error) {
            console.error('Error fetching creators:', error);
            commonToast(getApiError(error, t('creator_management.error_loading') || 'Failed to load creators'), 'error');
            setCreators([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCreators();
    }, []);

    useEffect(() => {
        setCurrentPage(0);
    }, [searchQuery, filterStatus, limit]);

    const handleEditCreator = (creator) => {
        if (!creator) return;
        setEditingCreator(creator);
        setIsEditOpen(true);
    };

    const handleDeleteCreator = (creator) => {
        if (!creator) return;
        setDeletingCreator(creator);
        setIsDeleteOpen(true);
    };

    const confirmDeleteCreator = async () => {
        const contentCreatorId = getContentCreatorId(deletingCreator);
        if (!contentCreatorId) {
            commonToast(t('creator_management.creator_not_found'), 'error');
            throw new Error('Missing contentCreatorId');
        }
        try {
            await deleteContentCreator(contentCreatorId);
            commonToast(t('creator_management.delete_success'), 'success');
            await fetchCreators();
        } catch (error) {
            commonToast(getApiError(error, t('creator_management.delete_failed')), 'error');
            throw error;
        }
    };

    const handleViewCreator = (creator) => {
        if (!creator) return;
        navigate(`/admin/creatordetail/${creator.id}`);
    };

    const handlePageChange = (selectedItem) => {
        setCurrentPage(selectedItem.selected);
    };

    const handleSearch = (e) => {
        setSearchQuery(e.target.value);
    };

    const handleStatusFilter = (selectedOption) => {
        setFilterStatus(selectedOption.value);
    };

    const filteredCreators = creators.filter(creator => {
        const needle = searchQuery.toLowerCase();
        const fullName = `${creator.firstName || ''} ${creator.lastName || ''} ${creator.name || ''} ${creator.email || ''} ${creator.username || ''}`.toLowerCase();
        const matchesSearch = !needle || fullName.includes(needle);
        const matchesStatus = filterStatus === 'all' || String(creator.status || '').toLowerCase() === filterStatus;
        return matchesSearch && matchesStatus;
    });
    const pageCount = Math.max(1, Math.ceil(filteredCreators.length / limit));
    const pagedCreators = filteredCreators.slice(currentPage * limit, currentPage * limit + limit);

    const statusOptions = [
        { value: 'all', label: t('all') },
        { value: 'active', label: t('creator_management.active') },
        { value: 'inactive', label: t('creator_management.inactive') }
    ];

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="bg-white rounded-lg shadow p-6">
                {/* Header */}
                <div className="flex flex-wrap justify-between items-center mb-6">
                    <h1 className="text-2xl font-bold text-gray-900">{t('creator_management.title')}</h1>
                    <div className="flex gap-4 md:mt-4 sm:flex-col">
                        {/* <button className="primary-btn">
                            <BiImport size={20} />
                            {t('creator_management.import_csv')}
                        </button> */}
                        <button
                            type="button"
                            onClick={() => setIsAddOpen(true)}
                            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                        >
                            <FaPlus size={20} />
                            {t('creator_management.add_creator')}
                        </button>
                    </div>
                </div>

                {/* Search and Filters */}
                <div className="flex flex-wrap gap-4 mb-6">
                    <div className="flex-1 min-w-[200px]">
                        <div className="relative">
                            <input
                                type="text"
                                placeholder={t('creator_management.search_placeholder')}
                                className="w-1/2 md:w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                value={searchQuery}
                                onChange={handleSearch}
                            />
                            <IoMdSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                        </div>
                    </div>
                    <div className="min-w-[200px]">
                        <Select
                            options={statusOptions}
                            placeholder={t('creator_management.filter_by_status')}
                            onChange={handleStatusFilter}
                            defaultValue={statusOptions[0]}
                            className="react-select-container"
                            classNamePrefix="react-select"
                        />
                    </div>
                </div>

                {/* Data Table */}
                <>
                    <TailwindDataTable
                        columns={columns}
                        data={pagedCreators}
                        thClasses='text-white'
                        loading={loading}
                    />

                    {/* Pagination Controls */}
                    <div className="flex justify-between items-center mt-4">
                        <div className='xs:mb-5'>
                            <PageSizeSelectBox selected={limit} setSelected={setLimit} setPage={() => setCurrentPage(0)} />
                        </div>
                        <div className='flex gap-4'>
                            <ReactPaginate
                                breakLabel="..."
                                nextLabel=">"
                                onPageChange={handlePageChange}
                                pageRangeDisplayed={5}
                                forcePage={Math.min(currentPage, pageCount - 1)}
                                pageCount={pageCount}
                                previousLabel="<"
                                renderOnZeroPageCount={null}
                                containerClassName="flex gap-2"
                                pageClassName="px-3 py-1 border rounded hover:bg-gray-100"
                                activeClassName="bg-blue-500 text-white"
                                previousClassName="px-3 py-1 border rounded hover:bg-gray-100"
                                nextClassName="px-3 py-1 border rounded hover:bg-gray-100"
                                disabledClassName="opacity-50 cursor-not-allowed"
                            />
                        </div>
                    </div>
                </>
                <AddAdminUserModel
                    open={isAddOpen}
                    setOpen={setIsAddOpen}
                    onCreated={fetchCreators}
                    role="creator"
                />
                <EditCreatorModel
                    open={isEditOpen}
                    setOpen={setIsEditOpen}
                    creator={editingCreator}
                    onUpdated={fetchCreators}
                />
                <DeleteConfirmationModel
                    open={isDeleteOpen}
                    setOpen={setIsDeleteOpen}
                    onConfirm={confirmDeleteCreator}
                    title="creator_management.delete_title"
                    message="creator_management.delete_confirm"
                    confirmText="Delete"
                    cancelText="Cancel"
                />
            </div>
        </div>
    );
}
